import React, { useState, useCallback, useEffect } from "react";
import { useAtomValue } from "jotai";
import { AIBlockService } from "frontend/services/ai-blocks/ai-block-service";
import { grammarFixerBlock } from "frontend/services/ai-blocks/edit-blocks/grammar-fixer-block";
import { getElement } from "shared/datamodel/canvas-element";
import { undoRedoStackAtom } from "state-atoms/stage-atoms";
import { syncServiceAtom } from "state-atoms/general-atoms";
import style from "./ai-edits-panel.module.css";
import { Icon } from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/toolbar-buttons";

interface GrammarFixerButtonProps {
  selectedElementIds: string[];
  onClose?: () => void;
  onError?: (error: string) => void;
}

export function GrammarFixerButton({
  selectedElementIds,
  onClose,
  onError,
}: GrammarFixerButtonProps): React.ReactElement {
  const [isProcessing, setIsProcessing] = useState(false);

  const undoRedoStack = useAtomValue(undoRedoStackAtom);
  const syncService = useAtomValue(syncServiceAtom);

  const aiBlockService = AIBlockService.getInstance();

  useEffect(() => {
    aiBlockService.registerBlock("grammar-fixer", grammarFixerBlock);
  }, []);

  const handleFixGrammar = useCallback(async () => {
    if (!syncService || selectedElementIds.length === 0) return;

    setIsProcessing(true);

    try {
      const reflect = syncService.getReplicache();
      if (!reflect) {
        onError?.("Canvas not ready");
        return;
      }

      const elementsData = await reflect.query(async (tx) => {
        const elements = await Promise.all(
          selectedElementIds.map(async (id) => {
            const element = await getElement(tx, id);
            return { id, element };
          })
        );
        return elements;
      });

      const { edits } = await aiBlockService.generateEdits("grammar-fixer", {
        prompt: "Fix grammar and spelling errors in the following texts",
        elementIds: elementsData.map((e) => e.id),
        texts: elementsData.map((e) => (e.element as { text?: string }).text || ""),
      });

      const editsMap = new Map(edits.map((e) => [e.elementId, e]));
      const idsToUpdate = selectedElementIds.filter((id) => editsMap.has(id));

      let elementIndex = 0;
      await undoRedoStack?.patchElements?.(
        idsToUpdate,
        (element) => {
          const elementId = idsToUpdate[elementIndex];
          const edit = editsMap.get(elementId);
          if (edit) {
            Object.assign(element, edit.patch);
          }
          elementIndex += 1;
        },
        `grammar-fixer-${Date.now()}`
      );

      onClose?.();
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : "Failed to fix grammar";
      onError?.(errorMessage);
      console.error("Error fixing grammar:", error_);
    } finally {
      setIsProcessing(false);
    }
  }, [syncService, selectedElementIds, aiBlockService, undoRedoStack, onClose, onError]);

  const isDisabled = selectedElementIds.length === 0 || isProcessing;

  return (
    <button
      className={style.actionButton}
      title="Fix grammar and spelling errors"
      onClick={handleFixGrammar}
      disabled={isDisabled}
    >
      <Icon icon="fix-grammer" />
      {isProcessing ? "Processing..." : "Fix Grammar & Spelling"}
    </button>
  );
}
